import api, { ClientInvestor } from '@api';
import { AccountBalance, CorporateFare, InfoOutlined } from '@mui/icons-material';
import {
    Button, ButtonProps, Divider, Tooltip, Typography
} from '@mui/material';
import { IconTypography, Loader, TextField } from '@tsp-ui/core/components';
import { replaceItemById, useAsyncEffect, usePageMessage } from '@tsp-ui/core/utils';
import { useActingClientID } from '@utils/hooks';
import { InvestorEligibilityContext } from '@views/admin/investor-eligibility/InvestorEligibilityRoutes';
import { InvestorFormFields } from '@views/admin/investors/InvestorDetailPage/components/InternalInvestorForm';
import clsx from 'clsx';
import {
    Dispatch, SetStateAction, useCallback, useContext, useEffect, useState
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import styles from './ClientInvestorForm.module.scss';


interface ClientInvestorFormProps {
    investorToEdit?: ClientInvestor;
    setLoading: Dispatch<SetStateAction<boolean>>;
    setDisableSubmit?: Dispatch<SetStateAction<boolean>>;
}

export default function ClientInvestorForm({ investorToEdit, setLoading, setDisableSubmit } : ClientInvestorFormProps) {
    const navigate = useNavigate();
    const clientID = useActingClientID();
    const pageMessage = usePageMessage();

    const { setInvestors } = useContext(InvestorEligibilityContext);

    const [ agencyInvestors, setAgencyInvestors ] = useState<ClientInvestor[]>();

    useAsyncEffect(useCallback(async () => {
        try {
            setAgencyInvestors(await api.client.investors.getAgencyInvestors());
        } catch (error) {
            pageMessage.handleApiError('An error occurred while fetching agency investors', error);
        }
    }, [ pageMessage ]));

    const formMethods = useForm<ClientInvestor>({
        defaultValues: investorToEdit || {
            addresses: [ {} ]
        }
    });

    const handleSubmit = formMethods.handleSubmit(async (formValues: ClientInvestor) => {
        if (clientID) {
            setLoading(true);

            try {
                const newInvestor = investorToEdit ? await api.client.investors.updateInvestor(formValues)
                    : isAgency
                        ? await api.client.investors.activateManagedInvestor({
                            sellerServicerNumber: formValues.sellerServicerNumber!,
                            investorId: agencyInvestorId
                        })
                        : await api.client.investors.addInvestor(formValues);

                setInvestors((investors = []) => (investorToEdit
                    ? replaceItemById(investors, newInvestor)
                    : investors.concat(newInvestor)));

                navigate(`../investors/${newInvestor.id}`);

                pageMessage.success(!formValues.id ? 'Investor added' : 'Investor details saved');
            } catch (error) {
                pageMessage.handleApiError('An error occurred while saving investor details', error);
            }

            setLoading(false);
        }
    });

    const isAgency = formMethods.watch('isAgency');
    const eligibilityBasedOnInvestorId = formMethods.watch('eligibilityBasedOnInvestorId');

    const [ agencyInvestorId, setAgencyInvestorId ] = useState('');

    const [ useAgencyBase, setUseAgencyBase ] = useState<boolean>();
    const allowSubmit = (isAgency && agencyInvestorId) || (
        isAgency === false && (useAgencyBase === false || eligibilityBasedOnInvestorId)
    );

    useEffect(() => {
        setDisableSubmit?.(!allowSubmit);
    }, [ allowSubmit, setDisableSubmit ]);

    const addInvestorContent = (
        <>
            <Typography align="center">
                Which type of investor would you like to add?
            </Typography>

            <div className={styles.agencyOrNonAgencyButtons}>
                <SelectableButton
                    selected={isAgency === true}
                    onClick={() => formMethods.setValue('isAgency', true)}
                    className={styles.agencyOrNonAgencyButton}
                >
                    <CorporateFare
                        color="secondary"
                        className={styles.icon}
                    />

                    Agency
                </SelectableButton>

                <SelectableButton
                    selected={isAgency === false}
                    onClick={() => formMethods.setValue('isAgency', false)}
                    className={styles.agencyOrNonAgencyButton}
                >
                    <AccountBalance
                        color="secondary"
                        className={styles.icon}
                    />

                    Non-Agency
                </SelectableButton>
            </div>

            {isAgency !== false && (
                <IconTypography
                    fontWeight={400}
                    variant="caption"
                    className={styles.help}
                    icon={(
                        <InfoOutlined
                            color="primary"
                            fontSize="small"
                        />
                    )}
                >
                    Base eligibility for agency investors is managed by Premicorr
                </IconTypography>
            )}

            {isAgency ? (
                <>
                    <Divider className={styles.divider} />

                    <Typography align="center">
                        Which agency investor would you like to activate?
                    </Typography>

                    {!agencyInvestors ? (
                        <Loader loading />
                    ) : (
                        <div className={styles.agencyButtons}>
                            {agencyInvestors.map(({ id, managedInvestorIsActive, name }) => (
                                <SelectableButton
                                    key={id}
                                    size="small"
                                    selected={agencyInvestorId === id}
                                    onClick={() => setAgencyInvestorId(id)}
                                    disabled={managedInvestorIsActive}
                                    title={managedInvestorIsActive
                                        ? 'This agency investor is already active'
                                        : ''}
                                >
                                    {name}
                                </SelectableButton>
                            ))}
                        </div>
                    )}

                    {agencyInvestorId && (
                        <TextField<ClientInvestor>
                            name="sellerServicerNumber"
                            label="Seller servicer number"
                            required
                        />
                    )}
                </>
            ) : isAgency !== undefined && (
                <>
                    <Divider className={styles.divider} />

                    <Typography align="center">
                        Would you like to base eligibility for this investor on an agency investor?
                    </Typography>

                    <div className={styles.yesNoButtons}>
                        <SelectableButton
                            size="small"
                            selected={useAgencyBase}
                            onClick={() => setUseAgencyBase(true)}
                        >
                            Yes
                        </SelectableButton>

                        <SelectableButton
                            size="small"
                            selected={useAgencyBase === false}
                            onClick={() => setUseAgencyBase(false)}
                        >
                            No
                        </SelectableButton>
                    </div>

                    <IconTypography
                        fontWeight={400}
                        variant="caption"
                        className={styles.help}
                        icon={(
                            <InfoOutlined
                                color="primary"
                                fontSize="small"
                            />
                        )}
                    >
                        Base eligibility for agency investors is managed by Premicorr
                    </IconTypography>

                    {useAgencyBase && (
                        <>
                            <Divider className={styles.divider} />

                            <Typography align="center">
                                Which agency investor would you like to use as a base?
                            </Typography>

                            {!agencyInvestors ? (
                                <Loader loading />
                            ) : (
                                <div className={styles.agencyButtons}>
                                    {agencyInvestors.map(({ id, name }) => (
                                        <SelectableButton
                                            key={id}
                                            size="small"
                                            selected={eligibilityBasedOnInvestorId === id}
                                            onClick={() => formMethods.setValue(
                                                'eligibilityBasedOnInvestorId', id
                                            )}
                                        >
                                            {name}
                                        </SelectableButton>
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    {(useAgencyBase === false || eligibilityBasedOnInvestorId) && (
                        <>
                            <Divider className={styles.divider} />

                            <Typography
                                align="center"
                                className={styles.investorDetailsLabel}
                            >
                                Enter details for the new investor
                            </Typography>

                            <div className={styles.formFields}>
                                <InvestorFormFields hideAgencySwitch />
                            </div>
                        </>
                    )}
                </>
            )}
        </>
    );

    return (
        <form
            id={ClientInvestorForm.formID}
            className={styles.root}
            onSubmit={handleSubmit}
            noValidate
        >
            <FormProvider {...formMethods}>
                {!investorToEdit ? addInvestorContent : isAgency
                    ? (
                        <TextField<ClientInvestor>
                            name="sellerServicerNumber"
                            label="Seller servicer number"
                            required
                        />
                    ) : (
                        <InvestorFormFields
                            hideAgencySwitch
                            disableCode
                        />
                    )}
            </FormProvider>
        </form>
    );
}

interface SelectableButtonProps extends ButtonProps {
    selected?: boolean;
    title?: string;
}

export function SelectableButton({
    className, children, selected, title, ...props
}: SelectableButtonProps) {
    return (
        <Tooltip title={title}>
            <span>
                <Button
                    variant="outlined"
                    {...props}
                    fullWidth
                    color={selected ? 'secondary' : 'primary'}
                    className={clsx(className, styles.selectableButton, {
                        [styles.selected]: selected
                    })}
                >
                    {children}
                </Button>
            </span>
        </Tooltip>
    );
}

ClientInvestorForm.formID = 'client-investor-form';
